import ClockIcon from "@svg/new-design/clock";
import Graduated from "@svg/new-design/graduated";
import { COURSE_TYPE, festival, STATUS } from "@types";
import { separateMoneyNumbers, toFarsiNumber } from "@utils/digits";
import { FC, useEffect, useMemo, useState, MouseEvent } from "react";
import { resolveProductImageSources } from "utils/product-image";
import { handleCourseUrl } from "@utils/handleCourseUrl";
import Link from "next/link";
import BookmarkBold from "@svg/new-design/bookmark-bold";
import Bookmark from "@svg/new-design/bookmark";
import { productCardDurationCalculator } from "@shared/components/card/helpers";
import { useCardAction } from "@shared/components/card/useCartAction";
import {
  ICON_COLOR,
  ICON_SIZE,
  ICON_VARIANT,
  IconButton,
} from "@shared/components/icon-button";
import ImageComponent from "@shared/components/image";
import CardFooter from "./CardFooter";

const InstructorProductCard: FC<{
  svg_featured_image: string;
  png_featured_image: string;
  svg_image: string;
  png_image: string;
  title: string;
  total_orders: number | undefined;
  is_buy: boolean;
  status: STATUS;
  price: number;
  watch_percent: number | undefined;
  id: number;
  festival_new: festival | undefined;
  hasCertificate: boolean;
  sku: string;
  in_cart: boolean;
  duration: {
    hours: number;
    in_minutes: number;
    minutes: number;
    rounded_in_hour: number;
  };
  slug: string;
  type: COURSE_TYPE;
  isFavorite: boolean;
}> = ({
  svg_image,
  png_image,
  title,
  total_orders,
  is_buy,
  status,
  price,
  watch_percent,
  id,
  festival_new,
  hasCertificate,
  sku,
  in_cart,
  duration,
  type,
  slug,
  isFavorite,
  svg_featured_image,
  png_featured_image,
}) => {
  const durationLabel = productCardDurationCalculator(duration);
  const renderClock = Boolean(durationLabel);

  const href = handleCourseUrl(type, slug || sku);
  const [favorite, setFavorite] = useState(isFavorite);
  const [isLoading, setIsLoading] = useState(false);

  const imageSources = useMemo(
    () =>
      resolveProductImageSources({
        svg_image,
        png_image,
        svg_featured_image,
        png_featured_image,
      }),
    [svg_image, png_image, svg_featured_image, png_featured_image]
  );

  useEffect(() => {
    setFavorite(isFavorite);
  }, [isFavorite]);
  const { addFavoriteCourse } = useCardAction();
  const handleFavorite = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setIsLoading(true);
    const res = await addFavoriteCourse?.(favorite, id);

    setIsLoading(false);
    setFavorite((prev) => (res ? !prev : prev));
  };
  return (
    <Link href={href} prefetch={false}>
      <div className="p-4 items-start  w-full hover:shadow-brand-md active:border-fds-brand rounded-8 border-fds-default border flex gap-x-4    ">
        <div className="rounded-4 grow shrink-0 relative h-[106px] aspect-video overflow-hidden">
          <div className="absolute right-0 z-[5] text-fds-secondary">
            <IconButton
              variant={ICON_VARIANT.DEFAULT}
              color={ICON_COLOR.DEFAULT}
              onClick={handleFavorite}
              icon={favorite ? <BookmarkBold /> : <Bookmark />}
              size={ICON_SIZE.MEDIUM}
              disabled={isLoading}
              aria-label={
                favorite ? "حذف از علاقمندی‌ها" : "افزودن به علاقمندی"
              }
              message={favorite ? "حذف از علاقمندی‌ها" : "افزودن به علاقمندی"}
            />
          </div>
          <ImageComponent
            fill
            src={imageSources.src}
            fallbackSrcs={imageSources.fallbackSrcs}
            alt={title}
          />
        </div>
        <div className="flex flex-col w-full ">
          <p className="line-clamp-1 title-lg mb-2  ">{title}</p>
          <div className="flex items-center">
            {total_orders ? (
              <div className="items-center flex gap-x-1 ">
                <Graduated className="w-5    h-5    text-fds-icon-tertiary" />
                <p className="body-md text-fds-secondary">
                  {`${separateMoneyNumbers(toFarsiNumber(total_orders))} `}
                  دانشجو
                </p>
              </div>
            ) : null}
            {Boolean(total_orders && renderClock) && (
              <div className="h-4     bg-fds-disable w-px mx-3  " />
            )}
            {renderClock ? (
              <div className="items-center flex gap-x-1 ">
                <ClockIcon className="w-5    h-5    text-fds-icon-tertiary " />
                <p className="body-md text-fds-secondary">
                  {toFarsiNumber(durationLabel)}
                </p>
              </div>
            ) : null}
          </div>
          <CardFooter
            isBuy={is_buy}
            status={status}
            price={price}
            watchPercent={watch_percent || 0}
            id={id}
            festival={festival_new}
            showShoppingCart={false}
            inCart={in_cart}
            sku={sku || ""}
            hasCertificate={hasCertificate}
          />
        </div>
      </div>
    </Link>
  );
};
export default InstructorProductCard;
